import React, { use, useEffect, useState } from 'react';
import PageContainer from '../../../components/PageContainer';
import MainContent from '../../../components/MainContent';
import PageTitle from '../../../components/Common/PageTitle/PageTitle';
import { useGym } from '../../../DataStore/Gym/GymContext';
import ActivityLoader from '../../../components/Common/ActivityLoader';
import GymAdmission from '../../../components/Gym/GymAdmission';
import ConfirmationDialog from '../../../components/Common/ConfirmationDialog';
import MembershipPaymentModal from '../../../components/Gym/MembershipPaymentModal';
import RegisterNewCustomerModal from '../../../components/Gym/RegisterNewCustomerModal';

const MemberFinderPage = () => {
  const {searchTerm, searchResults, setSearchTerm, getRecentMembers, recentMembers, getFrequentMembers, frequentMembers, handleEndSession} = useGym();
  const [showGymAdmission, setShowGymAdmission] = useState(false);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [showMembershipPayment, setShowMembershipPayment] = useState(false);
  const [showRegisterNewCustomer, setShowRegisterNewCustomer] = useState(false);

  useEffect(() => {
    getRecentMembers();
    getFrequentMembers();
  }, []);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  const initiateNewSession = (member) => {
    setCustomerInfo(member);
    setShowGymAdmission(true);
  };

  const newMembership = (member) => {
    setCustomerInfo(member);
    setShowMembershipPayment(true);
  };

  const promptEndSession = (member) => {
    setCustomerInfo(member);
    setShowWarning(true);
  };

  const handleOtherAction = (action, member) => {
    alert(`${action} for ${member.full_name}`); // Placeholder
  }

  const prepareMembershipPayment = (customer) => {
    setCustomerInfo(customer);
    setShowMembershipPayment(true);
  }

  const renderMemberCard = (member, buttonStyle = 'primary') => {
    const isEndSession = member.action_control === 'end_session';
    const btnClass = buttonStyle === 'outline-primary' ? (isEndSession ? 'btn-outline-danger' : 'btn-outline-primary') : (isEndSession ? 'btn-danger' : `btn-${buttonStyle}`);

    return (
      <div key={member.id} className="col-md-4 col-lg-3 mb-4">
        <div className="card text-center shadow-sm border-0 hover-shadow p-3" style={{ transition: '0.3s' }}>
          <div className="card-body d-flex flex-column align-items-center justify-content-start">
            <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center mb-3"
              style={{ width: '60px', height: '60px', fontSize: '20px' }}>
              {member.initials}
            </div>
            <h6 className="font-weight-bold mb-1">{member.full_name}</h6>
            <small className="text-muted mb-3">{member.phone_number}</small>
            <div className="btn-group-vertical w-100 gap-2">
              <button className={`btn ${btnClass} btn-sm rounded-pill mb-2`} onClick={() => isEndSession ? promptEndSession(member) : initiateNewSession(member)}>
                {isEndSession ? 'End Session' : 'Start Session'}
              </button>
              <button className="btn btn-outline-secondary btn-sm rounded-pill mb-2" onClick={() =>newMembership(member)}>Pay Membership</button>
              {/* <button className="btn btn-outline-info btn-sm rounded-pill" onClick={() => handleOtherAction('View History', member)}>View History</button> */}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (<>
    <PageContainer>
      <MainContent>
        <PageTitle
          title="Gym Customer Finder"
          description="Quickly find or register a gym member to start a session."
          iconClass="fa fa-search"
        />

        {/* Search Section */}
        <div className="card mb-4">
          <div className="card-body position-relative">
            <h5 className="font-weight-bold text-dark mb-3 search-heading">Search Member</h5>
            <div className="position-relative">
              <i className="fa fa-search position-absolute text-muted" style={{ left: '20px', top: '50%', transform: 'translateY(-50%)' }}></i>
              <input
                type="text"
                className="form-control form-control-lg rounded-pill shadow-sm pl-5 pr-5"
                placeholder="Search by name or phone number..."
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ paddingLeft: '50px', fontSize: '16px', paddingRight: searchTerm ? '50px' : '20px' }}
              />
              {searchTerm && (
                <button
                  className="btn position-absolute bg-transparent border-0"
                  style={{ right: '20px', top: '50%', transform: 'translateY(-50%)' }}
                  onClick={clearSearch}
                >
                  <i className="fa fa-times text-muted"></i>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Search Results Section */}
        {searchTerm.length >= 2 && (
          <div className="card mb-4">
            <div className="card-body">
              <h5 className="font-weight-bold text-dark mb-4 search-heading">Search Results {Array.isArray(searchResults) && (<small className="text-muted">({searchResults.length} found)</small>)}</h5>
              <div className="row">
                {Array.isArray(searchResults) ? (
                  searchResults.map(member => renderMemberCard(member, 'success'))
                ) : (
                  <div className="col-12 text-center text-muted">No members found. Please register new member</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Most Frequent Customers Section */}
        {Array.isArray(frequentMembers) && <div className="card mb-4">
          <div className="card-body">
            <h5 className="font-weight-bold text-dark mb-4 search-heading">Most Frequent Customers</h5>
            <div className="row">
              {frequentMembers.map(customer => renderMemberCard(customer, 'primary'))}
            </div>
          </div>
        </div>}

        {/* Register New Member Section */}
        <div className="card mb-4">
          <div className="card-body text-center">
            <h5 className="font-weight-bold text-primary mb-3">New Here?</h5>
            <p className="text-muted">Can't find the customer? Register a new member.</p>
            <button className="btn btn-success btn-lg rounded-pill px-5" onClick={()=>setShowRegisterNewCustomer(true)}>Register New Member</button>
          </div>
        </div>

        {/* Recent Members Section */}
        <div className="card mb-3">
          <div className="card-body">
            <h5 className="font-weight-bold text-primary mb-4 search-heading">Recent Members</h5>
            <div className="row">
              {Array.isArray(recentMembers) && recentMembers.map(member => renderMemberCard(member, 'outline-primary'))}
              {!recentMembers && <ActivityLoader />}
              {recentMembers === '404' && <div className="col-12 text-center text-muted">No recent members found.</div>}
            </div>
          </div>
        </div>
      </MainContent>
    </PageContainer>
    <GymAdmission
      show={showGymAdmission}
      onClose={() => setShowGymAdmission(false)}
      customer={customerInfo}
      onStartSession={(sessionDetails) => {
        console.log("Session Details: ", sessionDetails);
        setShowGymAdmission(false);
      }}
    />
    <ConfirmationDialog
      title="End Gym Session"
      alertDescription="Are you sure you want to end this gym session?"
      notes=""
      show={showWarning}
      handleClose={() => setShowWarning(false)}
      handleConfirm={() => handleEndSession(customerInfo)}
      buttonLabel="Confirm End"
    />

    <MembershipPaymentModal
      show={showMembershipPayment}
      onClose={() => setShowMembershipPayment(false)}
      customer = {customerInfo}
    />
     <RegisterNewCustomerModal
      show={showRegisterNewCustomer}
      onClose={() => setShowRegisterNewCustomer(false)}
      onComplete={prepareMembershipPayment}
    />

  </>);
};

export default MemberFinderPage;
